import { defineProperty } from '@stitch-fix/sf-next';

const oldWindowLocation = window.location;
/* eslint no-restricted-globals: "off" -- not relevant to test files */
const oldLocalStorage = window.localStorage;
const oldSessionStorage = window.sessionStorage;

type MockLocationOptions = {
  pathname?: string;
};

type MockedLocalStorage = { [key: string]: string | null };
type MockedSessionStorage = { [key: string]: string | null };

export const mockLocation = (options: MockLocationOptions = {}) => {
  const pathname = options.pathname || window.location.pathname;
  const replaceStub = jest.fn();

  defineProperty(window, 'location', {
    ...window.location,
    replace: replaceStub,
    pathname,
  });

  return {
    replaceStub,
  };
};

export const resetLocation = () => {
  defineProperty(window, 'location', oldWindowLocation);
};

/**
 * Test helper to mock local-storage
 */
export const mockLocalStorage = (
  mockedLocalStorage: MockedLocalStorage = {},
) => {
  // Need to explicitly return `null` to prevent the `useLocalStorageValue` hook from throwing a `JSON.parse(undefined)` error.
  // This matches the browser's `getItem` implementation when a key does not exist in local storage;
  const getItemStub = jest.fn(
    (key: string): string | null => mockedLocalStorage[key] ?? null,
  );
  const setItemStub = jest.fn();

  defineProperty(window, 'localStorage', {
    getItem: getItemStub,
    setItem: setItemStub,
  });

  return {
    getItemStub,
    setItemStub,
  };
};

/**
 * Test helper to mock reset local storage
 */
export const resetLocalStorage = () => {
  window.localStorage = oldLocalStorage;
};

/**
 * Test helper to mock sessionStorage
 */
export const mockSessionStorage = (
  mockedSessionStorage: MockedSessionStorage = {},
) => {
  // Need to explicitly return `null`
  // This matches the browser's `getItem` implementation when a key does not exist in session storage;
  const getItemStub = jest.fn(
    (key: string): string | null => mockedSessionStorage[key] ?? null,
  );
  const setItemStub = jest.fn();

  defineProperty(window, 'sessionStorage', {
    getItem: getItemStub,
    setItem: setItemStub,
  });

  return {
    getItemStub,
    setItemStub,
  };
};

/**
 * Test helper to mock reset sessionStorage
 */
export const resetSessionStorage = () => {
  window.sessionStorage = oldSessionStorage;
};

/**
 * Test helper to determine if the query parameter is true or another value. Useful in
 * differenciating between e2e test state like error, empty, and etc.
 */
export const hasQueryParam = (queryName: string, queryValue = 'true') => {
  const searchParams = new URLSearchParams(window.location.search);

  return searchParams.get(queryName) === queryValue;
};
